import { useCallback, useMemo } from 'react';
import { z, type ZodObjectDef, ZodType } from 'zod';

import { useQueryParams } from './use-query-params';

export type Filters<P extends {}> = {
  filters: P;
  update: OnFilterUpdate<P>;
};

type OnFilterUpdate<P extends {}> = <K extends keyof P>(
  key: K,
  value: P[K],
) => void;

export type FiltersMatch<R, P extends {}> = {
  [K in keyof P]: (result: R, value: NonNullable<P[K]>) => boolean;
};

export function useFilters<
  R,
  Z extends ZodType<any, ZodObjectDef, any>,
  P extends z.infer<Z>,
>(results: R[], schema: Z, filters: FiltersMatch<R, P>): [R[], Filters<P>] {
  const [params, queryParams] = useQueryParams(schema);

  const filteredResults = useMemo<R[]>(
    () =>
      results.filter((result) =>
        Object.entries(params).every(function ([key, value]) {
          if (value === undefined || value === null) {
            return true;
          }
          return filters[key](result, value);
        }),
      ),
    [results, params, filters],
  );

  const update = useCallback<OnFilterUpdate<P>>(
    function (key, value) {
      queryParams.replace({
        [key]: value,
        page: undefined,
        page_size: undefined,
      });
    },
    [queryParams],
  );

  return [filteredResults, { filters: params, update }];
}
